import { RightShipDialogServiceConstants } from './../../../../../../../../constants/basics/rightship-dialog.constants';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { DataGridColumn, DataType, ItemSource, SourceType } from "../../../../../../../../models/queries/queries.models";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { DataGrid } from "../../../../../../../components/data-grid/data-grid";
import { ConditionalDecimalPlacesValueConverter } from "../../../../converters/conditional-decimal-places-value-converter";
import { GetVesselEmissionsRequest } from "../../../../models/mep-port-models";


export class PortVesselsOdataGridViewModel {

    private _lastRequest = '';
    private _lastGetVesselEmissionsRequest: GetVesselEmissionsRequest;

    public itemSource: ItemSource;
    public dataGridVM: DataGrid;
    public allowedEmissionTypesColumns = [];

    public scientificNotationConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();

    constructor(private userClaimsService: UserClaimsService, private dialogClientService: DialogClientService) {
    }

    public refreshItemSource(request: GetVesselEmissionsRequest, forceRefresh: boolean = false) {

        const newRequest = `odata/VesselEmissions/Genesis.GetVesselEmissions(portId=${request.portId},` +
            `emissionTypeIds='${request.emissionTypeIds}',pointofInterestIds='${request.pointOfInterestIds}',` +
            `vesselTypeIds='${request.vesselTypeIds}',operationModeIds='${request.operationModeIds}',cargoTypeIds='${request.cargoTypeIds}',` +
            `safetyScores='${request.safetyScores}',ghgRatings='${request.ghgRatings}',weightBucketIds='${request.weightBucketIds}',` +
            `ageFrom=${request.ageFrom},ageTo=${request.ageTo},year=${request.year},months='${request.months}',searchFilter='${request.searchFilter}',` +
            `scopeIds='${request.scopeIds}')`;

        if (this._lastRequest == newRequest && !forceRefresh) {
            return; // no need to refresh everything is the same 
        }

        this._lastRequest = newRequest;
        this._lastGetVesselEmissionsRequest = request;

        const cols = this.columns;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: newRequest,
            columns: cols,
            initialFilters: [],
            initialSorting: []
        };
    }

    public get columns(): DataGridColumn[] {
        return [
            {
                columnOrder: 1,
                sortOrder: -1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'vesselName',
                title: 'Name',
                isSortable: true,
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href.bind="getHref(item)">${item.vesselName}</a>' +
                    '</div>' +
                    '<span>IMO ${item.imo}</span>' +
                    '</div>'
                ,
                parameter: {
                    item: null,
                    getHref: (item) => {
                        if (item.imo)
                            return "/vessel-details-by-imo/" + (item.imo);
                    }
                },
                width: "180px"
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" is-verified.bind="item.isGhgVerified"></comp-ghg-rating>',
                name: "ghgRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: 3,
                isSortable: true,
                parameter: {
                    item: null
                },
                isHidden: !this.userClaimsService.hasPermission(Claims.ShowGHG),
                tooltipForTitle: "This is the historical data at the day of last Vessel call at port for this FY"
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<span>${item.vesselTypeGroup}</span>',
                name: "vesselTypeGroup",
                title: "Type",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: 6,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 7,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                name: 'operationMode',
                title: 'Operation Mode',
                isSortable: true,
                instancePerRow: true,
                value: '<span>${item.operationMode}</span>',
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 8,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                name: 'pointOfInterest',
                title: 'Point of Interest',
                isSortable: true,
                instancePerRow: true,
                value: '<span>${item.pointOfInterest}</span>',
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 9,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                name: 'portCalls',
                title: 'Port Activity',
                isSortable: true,
                instancePerRow: true,
                value: '<div class="column">' +
                    '<span>${item.portCalls}</span>' +
                    '<a class="link" click.trigger="viewActivity(item)">View Activity</a>' +
                    '</div>'
                ,
                parameter: {
                    item: null,
                    viewActivity: (item) => {
                        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VesselPortActivityDialog, {
                            filters: {
                                portId: this._lastGetVesselEmissionsRequest.portId,
                                vesselIds: `${item.vesselSKey}`,
                                emissionTypeIds: this._lastGetVesselEmissionsRequest.emissionTypeIds,
                                pointOfInterestIds: this._lastGetVesselEmissionsRequest.pointOfInterestIds,
                                vesselTypeIds: this._lastGetVesselEmissionsRequest.vesselTypeIds,
                                operationModeIds: this._lastGetVesselEmissionsRequest.operationModeIds,
                                cargoTypeIds: this._lastGetVesselEmissionsRequest.cargoTypeIds,
                                safetyScores: this._lastGetVesselEmissionsRequest.safetyScores,
                                ghgRatings: this._lastGetVesselEmissionsRequest.ghgRatings,
                                weightBucketIds: this._lastGetVesselEmissionsRequest.weightBucketIds,
                                ageFrom: this._lastGetVesselEmissionsRequest.ageFrom,
                                ageTo: this._lastGetVesselEmissionsRequest.ageTo,
                                year: this._lastGetVesselEmissionsRequest.year,
                                months: this._lastGetVesselEmissionsRequest.months,
                                allowedEmissionTypes: this._lastGetVesselEmissionsRequest.allowedEmissionTypes
                            },
                            vesselData: item,
                            onClose: ()=> this.refreshItemSource(this._lastGetVesselEmissionsRequest)
                        }, true, false, false, "right");
                    }
                }
            },
            {
                columnOrder: 10,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.co2)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'co2',
                title: 'CO' + '2'.sub() + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('co2'.toLowerCase()) <= -1 // doing .tolower since allowedEmissionTypesColumns is always lower to ensure that we always observer that 
            },
            {
                columnOrder: 11,
                sortOrder: 7,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.cH4)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'cH4',
                title: `CH${'4'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('CH4'.toLowerCase()) <= -1
            },
            {
                columnOrder: 12,
                sortOrder: 8,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.n2o)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'n2o',
                title: `N${'2'.sub()}O` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('N2O'.toLowerCase()) <= -1
            },
            {
                columnOrder: 13,
                sortOrder: 9,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.totalGHGEmissions)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'totalGHGEmissions',
                title: 'Total GHG' + ' (tCO2e_100yr)',
                isSortable: true,
                instancePerRow: true,
                isHidden: !(this.allowedEmissionTypesColumns.indexOf('CO2'.toLowerCase()) > -1 && (this.allowedEmissionTypesColumns.indexOf('CH4'.toLowerCase()) > -1 || this.allowedEmissionTypesColumns.indexOf('N2O'.toLowerCase()) > -1))
            },
            {
                columnOrder: 14,
                sortOrder: 10,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.nox)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'nox',
                title: 'NO' + 'x'.sub() + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('nox'.toLowerCase()) <= -1
            },
            {
                columnOrder: 15,
                sortOrder: 11,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.sox)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'sox',
                title: 'SO' + 'x'.sub() + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('sox'.toLowerCase()) <= -1
            },
            {
                columnOrder: 16,
                sortOrder: 12,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.pm25)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'pm25',
                title: `PM${'2.5'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('pm2.5'.toLowerCase()) <= -1
            },
            {
                columnOrder: 17,
                sortOrder: 13,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.pm10)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'pm10',
                title: `PM${'10'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('pm10'.toLowerCase()) <= -1
            },
            {
                columnOrder: 18,
                sortOrder: 14,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.cH32C6H4)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'cH32C6H4',
                title: `(CH${'3'.sub()})${'2'.sub()}C${'6'.sub()}H${'4'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('(CH3)2C6H4'.toLowerCase()) <= -1
            },
            {
                columnOrder: 19,
                sortOrder: 15,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.co)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'co',
                title: 'CO' + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('co'.toLowerCase()) <= -1
            },
            {
                columnOrder: 20,
                sortOrder: 16,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.c6H5CH2CH3)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'c6H5CH2CH3',
                title: `C${'6'.sub()}H${'5'.sub()}CH${'2'.sub()}CH${'3'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('C6H5CH2CH3'.toLowerCase()) <= -1
            },
            {
                columnOrder: 21,
                sortOrder: 17,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.c7H8)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'c7H8',
                title: `C${'7'.sub()}H${'8'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('C7H8'.toLowerCase()) <= -1
            },
            {
                columnOrder: 22,
                sortOrder: 18,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.pAHs)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'pAHs',
                title: 'PAHs' + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('pahs'.toLowerCase()) <= -1
            },
            {
                columnOrder: 23,
                sortOrder: 19,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.c6H6)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'c6H6',
                title: `C${'6'.sub()}H${'6'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('C6H6'.toLowerCase()) <= -1
            },
            {
                columnOrder: 24,
                sortOrder: 20,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.cH2O)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'cH2O',
                title: `CH${'2'.sub()}O` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('CH2O'.toLowerCase()) <= -1
            },
            {
                columnOrder: 25,
                sortOrder: 21,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.c12H4Cl4O2)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'c12H4Cl4O2',
                title: `C${'12'.sub()}H${'4'.sub()}CI${'4'.sub()}O${'2'.sub()}` + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('C12H4Cl4O2'.toLowerCase()) <= -1
            },
            {
                columnOrder: 26,
                sortOrder: 22,
                dataType: DataType.CustomTemplate,
                value: '<span>${valueConverter(item.voCs)}</span>',
                parameter: {
                    item: null,
                    valueConverter: (value) => this.convertKgToTonnes(value)
                },
                name: 'vocs',
                title: 'VOCs' + ' (t)',
                isSortable: true,
                instancePerRow: true,
                isHidden: this.allowedEmissionTypesColumns.indexOf('vocs'.toLowerCase()) <= -1
            }
        ];
    }

    public convertKgToTonnes(value: number) {
        if (!value) return 0;
        const tonnesValue = value / 1000;
        return this.scientificNotationConverter.toView(tonnesValue, null)
    }
}
